import { useTranslations } from '@vocab/react';
import { Stack, Text, TextLink } from 'braid-design-system';
import { type FC, useEffect, useState } from 'react';

import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

import { SEEKPassAlert } from '../../SEEKPass';
import { seekPassFindOutMore } from '../../SEEKPass/urls';

import translations from './.vocab';

interface VerificationsAlertProps {
  onClose: () => void;
  onClickLearnMore: () => void;
}

export const VerificationsAlert: FC<VerificationsAlertProps> = ({
  onClose,
  onClickLearnMore,
}) => {
  const {
    getSeekPassVerificationsTabAlertStatus,
    hideSeekPassVerificationsTabAlert,
  } = useUserActionStatus('seek-pass-verifications-tab-alert');

  const [hidden, setHidden] = useState<boolean>(
    getSeekPassVerificationsTabAlertStatus() === 'hidden',
  );

  const { t } = useTranslations(translations);
  const { locale, site } = useAppConfig();

  useEffect(() => {
    if (hidden && getSeekPassVerificationsTabAlertStatus() !== 'hidden') {
      hideSeekPassVerificationsTabAlert();
    }
  }, [
    hidden,
    getSeekPassVerificationsTabAlertStatus,
    hideSeekPassVerificationsTabAlert,
  ]);

  if (hidden) {
    return null;
  }

  return (
    <SEEKPassAlert
      iconSize="standard"
      onClose={() => {
        setHidden(true);
        onClose();
      }}
      data={{ testid: 'verifications-alert' }}
    >
      <Stack space="medium">
        <Stack space="xsmall">
          <Text weight="strong" size="small">
            {t('The following credentials are verified by SEEK Pass')}
          </Text>
          <Text size="small">
            {t(
              'SEEK Pass gives you a simple, secure way to verify applicant credentials.',
            )}
          </Text>
        </Stack>
        <Text size="small" weight="strong">
          <TextLink
            href={seekPassFindOutMore({ locale, site })}
            onClick={onClickLearnMore}
            target="_blank"
          >
            {t('Learn how credentials are verified')}
          </TextLink>
        </Text>
      </Stack>
    </SEEKPassAlert>
  );
};
